import { createAudioSettings } from '~/utils/audioSettings'
import { onAppStateChange } from '~/composables/useAppLifecycle'
import { holdAudioBus } from '~/composables/useAudioBus'

/**
 * 🎵 Background music (v1.65, per Javi): one shared <audio> element,
 * looped, gentle volume. Browsers block autoplay without a gesture, so
 * a rejected play() arms a one-time pointerdown retry. The mute switch
 * persists across sessions.
 */
const MUTE_KEY = 'elemental-dispatch-music-muted'
const VOLUME_KEY = 'elemental-dispatch-music-volume'

/** ⚔️ Node fights draw one of these at random (v1.67; six since v5.72,
 * music by Javi). */
export const BATTLE_TRACKS = [
  '/music/battle1.mp3', '/music/battle2.mp3', '/music/battle3.mp3',
  '/music/battle4.mp3', '/music/battle5.mp3', '/music/battle6.mp3',
]
/** 👑 Boss rooms drew ONE looping song until v5.72 — now a pool of
 * three, so a long climb doesn't wear the same theme every act. */
export const BOSS_TRACKS = ['/music/boss.mp3', '/music/boss1.mp3', '/music/boss2.mp3']
/** 🍃 Everything that isn't a fight, a boss or the title (v1.67, per
 * Javi): map, draft, rewards — the wind-down songs. */
export const IDLE_TRACKS = ['/music/idle.mp3', '/music/idle1.mp3', '/music/idle2.mp3', '/music/idle3.mp3', '/music/idle4.mp3']

const preferences = createAudioSettings(VOLUME_KEY, MUTE_KEY)
const muted = preferences.muted
let el: HTMLAudioElement | null = null
let gestureArmed = false
let loaded = false
/** What's on: 'main' | 'battle' | 'boss' | 'idle'. Re-requesting the
 * SAME category keeps the current track rolling (map → reward → map
 * shouldn't restart the idle song); a new category swaps tracks. */
let currentCategory = ''

/** 🎵 v6.60: the tracks were re-encoded lighter IN PLACE (LAME VBR q5,
 * ~130 kb/s, from ~200) - the day-long /music cache needs a rev to let
 * go of the heavy files. Comparisons strip it again (isLoaded). */
const MUSIC_REV = 'v=2'
const trackUrl = (src: string) => new URL(`${src}${src.includes('?') ? '&' : '?'}${MUSIC_REV}`, window.location.origin).href
/** Is this pool path the track the element holds (query aside)? */
const isLoaded = (path: string) => !!el?.src && new URL(el.src).pathname === path

/** ⚡ v6.60 (Lighthouse): autoplay is refused until the first tap, but
 * assigning `src` and calling play() still DOWNLOADS the whole track -
 * 3.4 MB on the title screen, fetched for nothing while the page is
 * still loading. Until the page has a user activation (or the browser
 * says autoplay is allowed), the request is HELD and fired on the first
 * pointerdown; the last request before the tap wins, so a screen change
 * in between plays the right song. Nothing changes for the player -
 * the music started on that same first tap before. */
/**
 * ⏸ v6.65 (per Javi: "when the game is paused, can you pause the
 * music?"). Reasons to hold the song — a paused game, a backgrounded
 * tab — share ONE latch: the music stops when the first arrives and
 * resumes when the LAST clears, so unpausing while the tab is away (or
 * the reverse) can never restart it early. Only what was actually
 * playing resumes; a muted or never-started song stays silent.
 */
const suspendedBy = new Set<string>()
let resumeWhenClear = false
/** 📱 M6: a song is WANTED - loaded for a category, and not muted. */
const wantsToPlay = () => !!el?.src && !!currentCategory && !muted.value
function suspend(reason: string, on: boolean) {
  const had = suspendedBy.size > 0
  if (on) suspendedBy.add(reason)
  else suspendedBy.delete(reason)
  const has = suspendedBy.size > 0
  if (has === had) return
  if (has) {
    // 📱 M6 (mobile audit): latched on INTENT, not only on a playing
    // element - iOS pauses the element itself when the app leaves the
    // foreground, sometimes before this runs, and the song never came back.
    if (el && (!el.paused || wantsToPlay())) {
      resumeWhenClear = true
      el.pause()
    }
  } else if (resumeWhenClear) {
    resumeWhenClear = false
    tryPlay()
  }
}

let deferred: { src: string; category: string; force: boolean } | null = null
let deferArmed = false
function canAutoplayNow(): boolean {
  const nav = navigator as Navigator & { userActivation?: { hasBeenActive: boolean }; getAutoplayPolicy?: (type: 'mediaelement') => string }
  if (nav.userActivation?.hasBeenActive) return true
  if (typeof nav.getAutoplayPolicy === 'function') {
    try { return nav.getAutoplayPolicy('mediaelement') === 'allowed' } catch { return false }
  }
  return false
}
function deferUntilGesture(src: string, category: string, force: boolean) {
  deferred = { src, category, force }
  if (deferArmed) return
  deferArmed = true
  window.addEventListener('pointerdown', () => {
    deferArmed = false
    const d = deferred
    deferred = null
    if (d) start(d.src, d.category, d.force)
  }, { once: true })
}

function ensureLoaded() {
  if (loaded || typeof window === 'undefined') return
  loaded = true
  preferences.load()
  // 📵 Silence when the app leaves the foreground (v1.69, per Javi —
  // the song kept playing after switching away / closing the tab on
  // phone). Resume only what was playing when we come back. ⏸ v6.65:
  // that is now one of TWO reasons for the same silence (the other is a
  // paused game), so it goes through the shared latch below.
  document.addEventListener('visibilitychange', () => suspend('hidden', document.hidden))
  // 📱 M6: leaving the page goes through the latch too - a bare pause()
  // here left nothing to resume when the page came back.
  window.addEventListener('pagehide', () => suspend('hidden', true))
  window.addEventListener('pageshow', () => suspend('hidden', document.hidden))
  // 📱 M6: the app's own trip to the background (the native shell). The
  // song holds like a hidden tab, and the whole audio graph sleeps with it;
  // on the way back the graph wakes FIRST, then the song resumes.
  onAppStateChange((active) => {
    if (active) holdAudioBus(false)
    suspend('background', !active)
    if (!active) holdAudioBus(true)
  })
}

/** Categories that own several songs CYCLE through them (v1.76, per
 * Javi) — when a track ends, another random one from the pool follows.
 * Single-track categories (main) just loop. Boss joined the pools in
 * v5.72 when Javi's two new boss themes landed. */
const POOLS: Record<string, string[]> = {
  battle: BATTLE_TRACKS,
  idle: IDLE_TRACKS,
  boss: BOSS_TRACKS,
}

let wiredToBus = false
/** The track-swap envelope. User level/mute have their own following node:
 * routed audio must be muted on the graph because iOS ignores el.muted. */
let musicGain: GainNode | null = null
/** User level follows the track dip, so adjusting it cannot cancel a crossfade. */
let musicVolumeGain: GainNode | null = null
let categoryLevel = 0.45
let directVolumeTimer: ReturnType<typeof setTimeout> | null = null

function ensureEl(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null
  if (!el) {
    el = new Audio()
    el.loop = true
    el.volume = 0.45
    el.addEventListener('ended', () => {
      const pool = POOLS[currentCategory]
      if (!pool || !el) return
      const others = pool.filter(src => !isLoaded(src))
      const next = trackUrl((others[Math.floor(Math.random() * others.length)] ?? pool[0])!)
      // Through the dip as well (v2.66) — a track can end on a decaying
      // tail rather than true silence, so a bare swap clicks here too.
      swapSource(el, next, CATEGORY_VOLUME[currentCategory] ?? 0.45)
    })
  }
  // 🎚️ Route through the shared bus (v1.87): music and sfx leave as ONE
  // stream behind a limiter — the separate-stream mix popped on phones.
  if (!wiredToBus) {
    try {
      const bus = audioBus()
      if (bus) {
        const dip = bus.ctx.createGain()
        const volume = bus.ctx.createGain()
        bus.ctx.createMediaElementSource(el).connect(dip)
        dip.connect(volume)
        volume.connect(bus.master)
        musicGain = dip
        musicVolumeGain = volume
        musicGain.gain.value = 1
        musicVolumeGain.gain.value = muted.value ? 0 : preferences.volume.value
        wiredToBus = true
      }
    } catch { /* wiring is best-effort; the element still plays direct */ }
  }
  applyElementLevel()
  applyVolumeGain()
  return el
}

/** The category mix stays unscaled in memory. HTML-only playback applies the
 * preference exactly once, including when a pooled song ends. */
function applyElementLevel(smooth = false) {
  if (directVolumeTimer !== null) clearTimeout(directVolumeTimer)
  directVolumeTimer = null
  if (!el) return
  const audio = el
  if (wiredToBus) {
    audio.volume = categoryLevel
    audio.muted = false // The WebAudio volume gain owns mute on this path.
    return
  }
  const target = muted.value ? 0 : categoryLevel * preferences.volume.value
  if (!smooth || audio.paused) {
    audio.volume = target
    audio.muted = muted.value
    return
  }
  const initial = audio.volume
  let step = 0
  audio.muted = false
  const advance = () => {
    step++
    audio.volume = initial + (target - initial) * step / 4
    if (step < 4) directVolumeTimer = setTimeout(advance, 10)
    else {
      directVolumeTimer = null
      audio.muted = muted.value
    }
  }
  directVolumeTimer = setTimeout(advance, 10)
}

/** User volume and mute ramp independently of the track-swap envelope. */
function applyVolumeGain() {
  const bus = musicVolumeGain ? audioBus() : null
  if (!bus || !musicVolumeGain) return
  const now = bus.ctx.currentTime
  const g = musicVolumeGain.gain
  const current = g.value
  if (typeof g.cancelAndHoldAtTime === 'function') g.cancelAndHoldAtTime(now)
  else {
    g.cancelScheduledValues(now)
    g.setValueAtTime(current, now)
  }
  g.linearRampToValueAtTime(muted.value ? 0 : preferences.volume.value, now + 0.04)
}

function tryPlay() {
  const audio = ensureEl()
  if (!audio || !audio.src) return
  // ⏸ v6.65: nothing starts while the game is paused (or the tab is
  // away) — whatever wanted to play does, the moment that clears.
  if (suspendedBy.size > 0) {
    resumeWhenClear = true
    return
  }
  // The bus context must be RUNNING or the routed music is silent.
  audioBus()
  audio.play().catch(() => {
    // Autoplay refused — wait for the first tap, then start.
    if (gestureArmed) return
    gestureArmed = true
    window.addEventListener('pointerdown', () => {
      gestureArmed = false
      ensureEl()?.play().catch(() => {})
    }, { once: true })
  })
}

/* The battle tracks are mixed HOT — play them way down (v1.68, per
 * Javi). Everything else rides the standard level. */
const CATEGORY_VOLUME: Record<string, number> = { battle: 0.15 }

/** 🎚️ Swap tracks through a short gain dip (v2.66).
 *
 * Assigning `.src` stops the element DEAD on whatever sample it was
 * playing, and the level also steps between categories (battle rides at
 * 0.15, everything else 0.45). Through the shared bus both land as a
 * waveform discontinuity — the same click the sfx envelopes were fixed
 * for in v1.70/v1.74, just on the music path, which `npm run audit:audio`
 * never sees because it only renders the sfx recipes.
 *
 * Ramping the bus gain (not `.volume`, which is not automatable) means
 * every track change leaves and re-enters at silence. Falls back to a
 * plain swap when the element never wired to the bus.
 */
const DIP_SECONDS = 0.12

function swapSource(audio: HTMLAudioElement, url: string, level: number) {
  const bus = musicGain ? audioBus() : null
  if (!bus || !musicGain) {
    categoryLevel = level
    applyElementLevel()
    if (audio.src !== url) audio.src = url
    tryPlay()
    return
  }
  const now = bus.ctx.currentTime
  const g = musicGain.gain
  g.cancelScheduledValues(now)
  g.setValueAtTime(g.value, now)
  g.linearRampToValueAtTime(0.0001, now + DIP_SECONDS)
  setTimeout(() => {
    categoryLevel = level
    applyElementLevel()
    if (audio.src !== url) audio.src = url
    tryPlay()
    if (!musicGain) return
    const t = audioBus()!.ctx.currentTime
    musicGain.gain.cancelScheduledValues(t)
    musicGain.gain.setValueAtTime(0.0001, t)
    musicGain.gain.linearRampToValueAtTime(1, t + DIP_SECONDS)
  }, DIP_SECONDS * 1000)
}

function start(src: string, category: string, force = false) {
  const audio = ensureEl()
  if (!audio) return
  if (!canAutoplayNow()) {
    deferUntilGesture(src, category, force)
    return
  }
  // 🎵 v7.06 (Javi's First Dispatch report: battle music, then the title
  // theme the moment the card came up): a start that is ALLOWED supersedes
  // any gesture-deferred one. The title's theme, deferred on a fresh load
  // and never consumed when the run began from the keyboard, used to revive
  // on the first pointerdown of the fight and replace the battle track.
  deferred = null
  const level = CATEGORY_VOLUME[category] ?? 0.45
  // 🎵 v6.29 (per Javi: "make it so it doesn't reset the current song"):
  // a same-category request NEVER touches the track — the song plays on
  // through every screen change. A PAUSED element resumes where it left
  // off (backgrounded tab, an OS interruption); it used to fall through
  // and draw a fresh song, which is the hiccup Javi heard coming back to
  // the map. Only `force` (playRandom, i.e. a new fight) swaps.
  if (!force && currentCategory === category && audio.src) {
    categoryLevel = level
    applyElementLevel()
    if (audio.paused) tryPlay()
    return
  }
  currentCategory = category
  // Pooled categories advance via the 'ended' cycler; solo tracks loop.
  audio.loop = !POOLS[category]
  const url = trackUrl(src)
  if (audio.src === url && !audio.paused) {
    categoryLevel = level
    applyElementLevel()
    return
  }
  swapSource(audio, url, level)
}

export function useMusic() {
  ensureLoaded()
  return {
    muted,
    volume: preferences.volume,
    /** Start (or resume) a looping track; no-op if its category is
     * already playing. */
    play(src: string, category = src) {
      start(src, category)
    },
    /** A FRESH random track every time (v1.99, per Javi: "change the
     * music each time") — always swaps, never repeats the loaded one. */
    playRandom(category: string, tracks: string[]) {
      const fresh = el?.src ? tracks.filter(t => !isLoaded(t)) : tracks
      const pool = fresh.length > 0 ? fresh : tracks
      start(pool[Math.floor(Math.random() * pool.length)]!, category, true)
    },
    /** Keeps the current song when its category is already playing, and
     * draws a fresh one only when arriving from ANOTHER category. Every
     * non-fight screen uses this (v6.29): map, draft, collection, camps,
     * chests, the shop and puzzle halls all share the 'idle' category,
     * so the wind-down song runs unbroken across them — a fresh track
     * still lands after each fight (battle → idle is a real change) and
     * whenever one ends (the pool cycler). */
    ensure(category: string, tracks: string[]) {
      const fresh = el?.src ? tracks.filter(t => !isLoaded(t)) : tracks
      const pool = fresh.length > 0 ? fresh : tracks
      start(pool[Math.floor(Math.random() * pool.length)]!, category)
    },
    stop() {
      el?.pause()
      currentCategory = ''
      deferred = null
      resumeWhenClear = false
    },
    /** ⏸ v6.65: hold the song while the game is paused. */
    setPaused(on: boolean) {
      suspend('paused', on)
    },
    toggleMute() {
      preferences.toggleMute()
      applyElementLevel(true)
      applyVolumeGain()
      // Unmuting counts as a gesture — if autoplay was blocked before,
      // this tap is the moment the track can actually start.
      if (el && !muted.value && el.paused && el.src) tryPlay()
    },
    setVolume(value: number) {
      preferences.setVolume(value)
      applyElementLevel(true)
      applyVolumeGain()
      if (el && !muted.value && el.paused && el.src) tryPlay()
    },
  }
}
